/* ================================= */
/*            Event Based            */
/* ================================= */

import {
  CodeExampleSDK,
  CodeExampleUsecases,
} from "@/feature/components/FeatureCodeExample";

type CodeExamplesMap = Record<
  CodeExampleSDK,
  Record<CodeExampleUsecases, string>
>;

/**
 * React
 */

const reactCommon = (publishableKey: string) => /*jsx*/ `
import { ReflagProvider, useFlag } from "@reflag/react-sdk";

// Initialize SDK as a React context provider.
const App = () => (
  <ReflagProvider
    publishableKey="${publishableKey}"
    user={{ id: "john_doe", name: "John Doe" }}
    company={{ id: "acme_inc", name: "Acme Inc." }}
  >
    <MyFeature />
  </ReflagProvider>
);
`;

const reactAccess = (flagKey: string) => /*jsx*/ `
const MyFeature = () => {
  const { isEnabled } = useFlag("${flagKey}");

  // Check if the flag is enabled for user.
  return isEnabled ? "You have access!" : null;
};
`;

const reactAdoption = (flagKey: string) => /*jsx*/ `
const MyFeature = () => {
  const { track } = useFlag("${flagKey}");

  // Track feature adoption for the user.
  return <button onClick={() => track()}>Feature used!</button>;
};
`;

const reactFeedback = (flagKey: string) => /*jsx*/ `
const MyFeature = () => {
  const { requestFeedback } = useFlag("${flagKey}");

  // Ask a customer for feedback.
  return (
    <button
      onClick={(e) =>
        requestFeedback({
          title: "Do you like this feature?",
          position: {
            type: "POPOVER",
            anchor: e.currentTarget as HTMLElement,
          },
        })
      }
    >
      Give feedback
    </button>
  );
};
`;

const reactConfig = (flagKey: string) => /*jsx*/ `
const MyComponent = () => {
  const { config: { key, payload } } = useFlag("${flagKey}");

  // Print the configuration and payload.
  return (
    <div>
      <h3>Configuration: {key}</h3>
      <pre>
        {JSON.stringify(payload)}
      </pre>
    </div>
  );
};
`;

const reactMonitor = (flagKey: string) => /*jsx*/ `
const MyFeature = () => {
  const { track, requestFeedback } = useFlag("${flagKey}");

  return (
    <>
      {/* Track feature adoption for the user. */}
      <button onClick={() => track()}>Feature used!</button>;
      {/* Ask a customer for feedback. */}
      <button
        onClick={(e) =>
          requestFeedback({
            title: "Do you like this feature?",
            position: {
              type: "POPOVER",
              anchor: e.currentTarget as HTMLElement,
            },
          })
        }
      >
        Give feedback
      </button>
    </>
  );
};
`;

/**
 * Browser
 */

const browserCommon = (publishableKey: string) => /*js*/ `
import { ReflagClient } from "@reflag/browser-sdk";

// Setup and configure the SDK.
const reflagClient = new ReflagClient({
  publishableKey: "${publishableKey}",
  user: { id: "john_doe", name: "John Doe" },
  company: { id: "acme_inc", name: "Acme Inc." }
});
await reflagClient.initialize();
`;

const browserAccess = (flagKey: string) => /*js*/ `
const { isEnabled } = reflagClient.getFlag("${flagKey}");

// Check if the flag is enabled for the user.
if (isEnabled) {
  console.log("You have access!");
}
`;

const browserAdoption = (flagKey: string) => /*js*/ `
const { track } = reflagClient.getFlag("${flagKey}");
const button = document.querySelector("button");
button.addEventListener("click", (e) => {
  // Track feature adoption for the user.
  track();
});
`;

const browserFeedback = (flagKey: string) => /*js*/ `
const { requestFeedback } = reflagClient.getFlag("${flagKey}");
const button = document.querySelector("button");
button.addEventListener("click", (e) => {
  // Ask a customer for feedback.
  requestFeedback({
    title: "Do you like this feature?",
    position: {
      type: "POPOVER",
      anchor: e.currentTarget as HTMLElement,
    },
  });
});
`;

const browserConfig = (flagKey: string) => /*js*/ `
const { config: { key, payload } } = reflagClient.getFlag("${flagKey}");

// Print the configuration and payload.
console.log(\`Using configuration: \${key}, with payload: \${payload}\`);
`;

const browserMonitor = (flagKey: string) => /*js*/ `
const { track, requestFeedback } = reflagClient.getFlag("${flagKey}");

// Track feature adoption for the user.
document.querySelector("button.track")
  .addEventListener("click", (e) => {
    track();
  });

// Ask a customer for feedback.
document.querySelector("button.feedback")
  .addEventListener("click", (e) => {
    requestFeedback({
      title: "Do you like this feature?",
      position: {
        type: "POPOVER",
        anchor: e.currentTarget as HTMLElement,
      },
    });
  });
`;

/**
 * Node
 */

const nodeCommon = (secretKey: string) => /*js*/ `
import { ReflagClient } from "@reflag/node-sdk";

// Setup and configure the SDK.
const reflagClient = new ReflagClient({
  secretKey: "${secretKey}",
});
await reflagClient.initialize();

// Create a bound instance for a specific user and company.
const boundClient = reflagClient.bindClient({
  user: { id: "john_doe", name: "John Doe" },
  company: { id: "acme_inc", name: "Acme Inc." },
});
`;

const nodeAccess = (flagKey: string) => /*js*/ `
const { isEnabled } = boundClient.getFeature("${flagKey}");

// Check if the flag is enabled for the user.
if (isEnabled) {
  console.log("User has access!");
}
`;

const nodeAdoption = (flagKey: string) => /*js*/ `
const { track } = boundClient.getFeature("${flagKey}");

// Track feature adoption for the user.
track();
`;

const nodeFeedback = (_flagKey: string) => /*js*/ `
/**
 * Currently not available in the Node.js SDK.
 */
`;

const nodeConfig = (flagKey: string) => /*js*/ `
const { config: { key, payload } } = boundClient.getFeature("${flagKey}");

// Print the configuration and payload.
console.log(\`Using configuration: \${key}, with payload: \${payload}\`);
`;

const nodeMonitor = (flagKey: string) => /*js*/ `
const { track } = boundClient.getFeature("${flagKey}");

// Track feature adoption for the user.
track();
`;

/**
 * Export
 */

export const codeExamplesByUsecase = (
  flagKey: string,
  publishableKey: string,
  secretKey: string,
): CodeExamplesMap => ({
  react: {
    access: reactCommon(publishableKey) + reactAccess(flagKey),
    usage: reactCommon(publishableKey) + reactAdoption(flagKey),
    feedback: reactCommon(publishableKey) + reactFeedback(flagKey),
    config: reactCommon(publishableKey) + reactConfig(flagKey),
    monitor: reactCommon(publishableKey) + reactMonitor(flagKey),
  },
  node: {
    access: nodeCommon(secretKey) + nodeAccess(flagKey),
    usage: nodeCommon(secretKey) + nodeAdoption(flagKey),
    feedback: nodeFeedback(flagKey),
    config: nodeCommon(secretKey) + nodeConfig(flagKey),
    monitor: nodeCommon(secretKey) + nodeMonitor(flagKey),
  },
  browser: {
    access: browserCommon(publishableKey) + browserAccess(flagKey),
    usage: browserCommon(publishableKey) + browserAdoption(flagKey),
    feedback: browserCommon(publishableKey) + browserFeedback(flagKey),
    config: browserCommon(publishableKey) + browserConfig(flagKey),
    monitor: browserCommon(publishableKey) + browserMonitor(flagKey),
  },
});
